import { useEffect, useState } from 'react';

const EMAIL_REGEX = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;

/** Minimum digits for French international format (+33 X XX XX XX XX) */
const MIN_FRENCH_PHONE_DIGITS = 9;

/** French national format: 10 digits starting with 0 */
const FRENCH_NATIONAL_LENGTH = 10;

/**
 * Returns digits only from phone string
 * @param {string} phone - Phone string
 * @returns {string}
 */
const getPhoneDigits = (phone) => (phone || '').replace(/\D/g, '');

/**
 * Returns true if the phone matches a valid French format (national 0X XX XX XX XX or international +33).
 * @param {string} phone - Phone string
 * @returns {boolean}
 */
export const isFrenchPhoneFormat = (phone) => {
	const digits = getPhoneDigits(phone);
	if (digits.length === FRENCH_NATIONAL_LENGTH && digits[0] === '0' && digits[1] >= '1' && digits[1] <= '9') return true;
	if (digits.length === MIN_FRENCH_PHONE_DIGITS && digits[0] >= '1' && digits[0] <= '9') return true;
	if (digits.length === 11 && digits.startsWith('33') && digits[2] >= '1' && digits[2] <= '9') return true;
	return false;
};

/**
 * Returns true if the phone has content but is too short for French format
 * @param {string} phone - Phone string
 * @returns {boolean}
 */
export const isPhoneTooShort = (phone) => {
	const digits = getPhoneDigits(phone);
	return digits.length > 0 && digits.length < MIN_FRENCH_PHONE_DIGITS;
};

/**
 * Returns true if the phone has enough digits but is not a valid French format
 * @param {string} phone - Phone string
 * @returns {boolean}
 */
export const isFrenchPhoneFormatInvalid = (phone) => {
	const digits = getPhoneDigits(phone);
	return digits.length >= MIN_FRENCH_PHONE_DIGITS && !isFrenchPhoneFormat(phone);
};

const isPhoneValid = (phone) => {
	if (!phone || (phone || '').trim() === '') return false;
	return isFrenchPhoneFormat(phone);
};

/**
 * Validates billing information when billing is different from delivery
 * @param {object} cart - Cart object
 * @param {boolean} isSameInfo - Whether billing info is same as delivery
 * @returns {boolean} True if billing info is valid
 */
const validateBillingInfo = (cart, isSameInfo) => {
	if (isSameInfo) return true;

	if (!cart.billing_address || cart.billing_address === '') return false;
	if (!isPhoneValid(cart.billing_phone)) return false;
	if (!cart.billing_firstname || cart.billing_firstname === '') return false;
	if (!cart.billing_lastname || cart.billing_lastname === '') return false;

	return true;
};

/**
 * Validates delivery information
 * @param {object} cart - Cart object
 * @returns {boolean} True if delivery info is valid
 */
const validateDeliveryInfo = (cart) => {
	if (!cart.delivery_date || cart.delivery_date === '') return false;
	if (!cart.delivery_firstname || cart.delivery_firstname === '') return false;
	if (!cart.delivery_lastname || cart.delivery_lastname === '') return false;
	if (!cart.delivery_address || !cart.delivery_address.address || cart.delivery_address.address === '') return false;
	if (!isPhoneValid(cart.delivery_phone)) return false;

	return true;
};

/**
 * Validates email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if email is valid
 */
const validateEmail = (email) => {
	if (!email || email === '') return false;
	return EMAIL_REGEX.test(email);
};

/**
 * Runs full payment form validation from /commande/paiement.
 * Returns a result object for use when submitting or validating explicitly.
 * @param {object} cart - Cart object
 * @param {boolean} isSameInfo - Whether billing info matches delivery
 * @returns {{ valid: boolean, errorMessage?: string }}
 */
export const validatePaymentForm = (cart, isSameInfo) => {
	if (!cart) return { valid: false, errorMessage: 'Formulaire incomplet' };

	if (!validateDeliveryInfo(cart)) {
		if (!isPhoneValid(cart.delivery_phone)) {
			return { valid: false, errorMessage: 'Téléphone de livraison : format français requis (0X XX XX XX XX)' };
		}
		return { valid: false, errorMessage: 'Veuillez remplir toutes les informations de livraison' };
	}

	if (!validateBillingInfo(cart, isSameInfo)) {
		if (!isSameInfo && !isPhoneValid(cart.billing_phone)) {
			return { valid: false, errorMessage: 'Téléphone de facturation : format français requis (0X XX XX XX XX)' };
		}
		return { valid: false, errorMessage: 'Veuillez remplir toutes les informations de facturation' };
	}

	if (!validateEmail(cart.billing_email)) {
		return { valid: false, errorMessage: 'Email invalide' };
	}

	return { valid: true };
};

/**
 * Custom hook for payment form validation
 * @param {object} cart - Cart object
 * @param {function} setCart - Function to update cart
 * @param {boolean} isSameInfo - Whether billing info is same as delivery
 * @returns {{canSubmit: boolean}} Validation result
 */
export const usePaymentValidation = (cart, setCart, isSameInfo) => {
	const [canSubmit, setCanSubmit] = useState(false);

	useEffect(() => {
		if (!validateBillingInfo(cart, isSameInfo)) {
			setCanSubmit(false);
			setCart((prevState) => ({
				...prevState,
				canSubmitPayment: false
			}));
			return;
		}

		if (!validateDeliveryInfo(cart)) {
			setCanSubmit(false);
			setCart((prevState) => ({
				...prevState,
				canSubmitPayment: false
			}));
			return;
		}

		if (!validateEmail(cart.billing_email)) {
			setCanSubmit(false);
			setCart((prevState) => ({
				...prevState,
				canSubmitPayment: false
			}));
			return;
		}

		setCanSubmit(true);
		setCart((prevState) => ({
			...prevState,
			canSubmitPayment: true
		}));
	}, [
		isSameInfo,
		cart?.delivery_date,
		cart?.delivery_firstname,
		cart?.delivery_lastname,
		cart?.delivery_address,
		cart?.delivery_phone,
		cart?.billing_address,
		cart?.billing_phone,
		cart?.billing_firstname,
		cart?.billing_lastname,
		cart?.billing_email,
		setCart,
	]);

	return { canSubmit };
};

